import { useEffect } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

// third party
import { IconAlertCircle, IconLoader2 } from '@tabler/icons-react';
import { useIntl } from 'react-intl';

// project imports
import { useDispatch, useSelector } from 'store';
import { clearGlobalStatusError, selectGlobalStatusDisplay } from 'store/slices/global-status';

const ICON_PX = 18;
const ERROR_FLASH_MS = 2800;

// ==============================|| HEADER GLOBAL STATUS (AXIOS) ||============================== //

export default function GlobalStatusIndicator() {
  const theme = useTheme();
  const intl = useIntl();
  const status = useSelector(selectGlobalStatusDisplay);
  const dispatch = useDispatch();

  useEffect(() => {
    if (status !== 'error') return;
    const t = window.setTimeout(() => {
      dispatch(clearGlobalStatusError());
    }, ERROR_FLASH_MS);
    return () => window.clearTimeout(t);
  }, [status, dispatch]);

  const isIdle = status === 'idle';

  const color =
    status === 'loading' ? theme.palette.primary.main : status === 'error' ? theme.palette.warning.main : 'transparent';

  const tooltipId =
    status === 'loading'
      ? 'logoSection.globalRequestLoadingTooltip'
      : status === 'error'
        ? 'logoSection.globalRequestErrorTooltip'
        : undefined;

  const ariaLabel = tooltipId ? intl.formatMessage({ id: tooltipId }) : '';

  return (
    <Tooltip title={ariaLabel} disableHoverListener={isIdle}>
      <Box
        component="span"
        role={isIdle ? undefined : 'status'}
        aria-live={isIdle ? undefined : 'polite'}
        aria-busy={status === 'loading'}
        aria-label={isIdle ? undefined : ariaLabel}
        aria-hidden={isIdle}
        sx={{
          position: 'relative',
          width: ICON_PX,
          height: ICON_PX,
          minWidth: ICON_PX,
          minHeight: ICON_PX,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginInlineStart: 1,
          color: isIdle ? 'transparent' : color,
          cursor: 'default',
          visibility: isIdle ? 'hidden' : 'visible',
          '@keyframes globalStatusSpin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
          ...(status === 'loading' && {
            animation: 'globalStatusSpin 0.85s linear infinite',
          }),
        }}
      >
        {status === 'error' ? (
          <IconAlertCircle stroke={1.75} size={ICON_PX} aria-hidden />
        ) : (
          <IconLoader2 stroke={1.75} size={ICON_PX} aria-hidden />
        )}
      </Box>
    </Tooltip>
  );
}
